// Profil-Nachbau F-25 (output/profil-team-20261014/cockpit-grafiken-20261019/SPEC.md 2.1, SCHNITTSTELLEN.md 5.1; Entwurf A
// mittel/bausteine.js CL.saeulen, CL.linien): Säulen (eine Reihe oder zwei gestapelt) und Linie als SVG ohne Diagrammbibliothek
// (E-25-1), mit Teilstrichen, Gitter, Achsen, Tooltip per Maus, Finger und Tastatur, Wochenend-Bändern, Schnitt- und Vorher-Linie,
// Beschriftung des höchsten Abschnitts und hinterlegten Abschnitten für die Satzbezüge (Entwurf A). Farben nur aus Tokens
// (diagramm.css). Der Baustein formatiert keine Beträge: Werte- und Achsentext gibt der Aufrufer.
// F-25 Entwurf C (PF-GC-baustein-2): dazu die Zielkurve – mehrere Linien mit Lücken und Strichart, Fläche und Endwert je Reihe,
// Ziellinie für Linien und Säulen, Linienlegende.
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { zoomVon } from '../../components/Erklaerung';
import { DIAGRAMM } from './cockpitTexte';
import { abschnittText, achseText, monatName, tagKurz, teilstriche, tippKopf, wochentag, zahl } from './format';
import type { Einheit } from './cockpitApi';
import './diagramm.css';

export type DiagrammFarbe = 'recherchen' | 'nachfragen' | 'ohneQuelle' | 'linie' | 'vorzeitraum';
export type DiagrammAbschnitt = { von: string; bis: string; werte: (number | null)[] };   // je Reihe ein Wert oder null (kein Wert an dem Tag)
export type DiagrammReihe = { name: string; farbe: DiagrammFarbe; strich?: 'voll' | 'gestrichelt' | 'punktiert'; flaeche?: boolean;
  endText?: string };                                                                     // strich, flaeche, endText nur bei art 'linie'
export type DiagrammLinie = { wert: number; text: string; art: 'schnitt' | 'vorher' | 'ziel'; seite?: 'links' | 'rechts' };
export type TippInhalt = { kopf: string; text?: string; zeilen?: { farbe?: DiagrammFarbe; name: string; wert: string }[]; fuss?: string };
export type DiagrammProps = {
  art: 'saeulen' | 'linie';
  name: string;                                 // Anfang des Satzes für Vorlesehilfen, meist der Titel der Karte
  einheit: Einheit;
  abschnitte: DiagrammAbschnitt[];
  reihen: DiagrammReihe[];                      // Säulen: eine oder zwei gestapelt (unten die erste); Linie: je sichtbare Reihe eine, die erste oben
  sichtbar?: boolean[];                         // Häkchen der Legende; Standard alle an
  heute: string;                                // JJJJ-MM-TT in Wien, für „(heute, bis jetzt)“
  wertText?: (wert: number) => string;          // Werte im Tooltip, Standard zahl(); Plattform-Kosten über netMoney
  achsenText?: (wert: number) => string;        // Y-Achse, Standard zahl()
  skala?: number;                               // Teilstriche als Vielfache (Kosten in Cent: 100)
  fuss?: (index: number) => string | undefined; // letzte Zeile im Tooltip
  linien?: DiagrammLinie[];                     // waagrechte Linien mit Beschriftung („Schnitt 4,8“, „davor 4,5“)
  hoechstwert?: (index: number, wert: number) => string;  // Beschriftung über dem höchsten Abschnitt („13 Recherchen am Di. 13.10.“)
  hoechstIndex?: number;                        // Abschnitt der Beschriftung, wenn nicht der höchste gestapelte (Nachtrag 12:40, cockpit-3 D-6)
  wochenenden?: boolean;                        // Bänder an Samstag und Sonntag, nur Einheit TAG
  markiert?: (index: number) => boolean;        // hinterlegte Abschnitte (Satzbezug „dienstags“)
  zeige?: number | null;                        // von außen geöffneter Tooltip (Satzbezug „Dienstag, 13. Oktober“); null schließt
  vorlesen: string;                             // Satz für role="img", z. B. „Recherchen je Tag, 20.09. bis 19.10.2026: höchstens 14 am 07.10., zusammen 128“
  hoehe?: { breit: number | 'fenster'; schmal: number };   // 'fenster' = Math.max(200, Math.min(300, innerHeight - 540)); schmal unter 700 px
  flaeche?: boolean;                            // Linie mit Fläche unter der ersten Reihe (je Reihe: DiagrammReihe.flaeche)
  beschriftung?: (index: number) => { achse: string; kopf: string };  // ersetzt Datum an Achse und Tooltip-Kopf (Häufigkeit je Person, teile-1)
  tipp?: (index: number) => TippInhalt;         // ersetzt den ganzen Tooltip-Inhalt (Häufigkeit, Quellen mit Auswahl)
  maxBreite?: number;                           // größte Säulenbreite in px, Standard 24 (Häufigkeit: 64)
};

// Im Browser vor dem Zeichnen messen; auf dem Server (Unit-Tests mit renderToStaticMarkup) ohne Warnung.
const useMessEffekt = typeof window === 'undefined' ? useEffect : useLayoutEffect;
// null (kein Wert an dem Tag) zählt als 0.
const summe = (werte: (number | null)[]) => werte.reduce<number>((s, wert) => s + (wert ?? 0), 0);
const rund = (wert: number) => Math.round(wert * 10) / 10;
const enthaeltHeute = (abschnitt: DiagrammAbschnitt, heute: string) => abschnitt.von <= heute && heute <= abschnitt.bis;

/** Pfad einer Säule von unten nach oben; mit rund die oberen Ecken 4 px rund (Entwurf A bausteine.js saeule). */
function saeule(x: number, oben: number, breite: number, hoehe: number, rundOben: boolean): string {
  if (hoehe <= 0) return '';
  const r = Math.min(rundOben ? 4 : 0, hoehe, breite / 2);
  const [l, o, b, u] = [rund(x), rund(oben), rund(x + breite), rund(oben + hoehe)];
  return `M${l},${u} V${rund(oben + r)} Q${l},${o} ${rund(x + r)},${o} H${rund(x + breite - r)} Q${b},${o} ${b},${rund(oben + r)} V${u} Z`;
}

/** Satz für Vorlesehilfen: „Recherchen je Tag, 20.09. bis 19.10.2026: höchstens 14 am 07.10., zusammen 128.“ */
export function diagrammSatz(name: string, einheit: Einheit, abschnitte: DiagrammAbschnitt[], wertText?: (wert: number) => string): string {
  if (!abschnitte.length) return `${name}.`;
  const text = wertText ?? zahl;
  const summen = abschnitte.map(abschnitt => summe(abschnitt.werte));
  // Bei Gleichstand der letzte Abschnitt (wie hoechsterAbschnitt in bericht.ts).
  const hoechst = summen.reduce((beste, wert, i) => wert >= summen[beste] ? i : beste, 0);
  const a = abschnitte[hoechst];
  const wann = einheit === 'TAG' ? DIAGRAMM.amTag(tagKurz(a.von)) : einheit === 'WOCHE' ? DIAGRAMM.inWoche(tagKurz(a.von)) : DIAGRAMM.imMonat(monatName(a.von));
  const zeitraum = abschnittText('WOCHE', abschnitte[0].von, abschnitte[abschnitte.length - 1].bis);
  return DIAGRAMM.satz(name, zeitraum, text(summen[hoechst]), wann, text(summe(summen)));
}

/** Text der aria-live-Zeile: „{Kopf}: {Name} {Wert}, … {Fuß}.“ */
function vorlesText(inhalt: TippInhalt): string {
  const zeilen = (inhalt.zeilen ?? []).map(zeile => `${zeile.name} ${zeile.wert}`).join(', ');
  const teile = [inhalt.text, zeilen].filter(Boolean).join(' ');
  const text = `${inhalt.kopf}: ${teile}${teile && !/[.!?]$/.test(teile) && inhalt.fuss ? '.' : ''}${inhalt.fuss ? ` ${inhalt.fuss}` : ''}`;
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/** Gestalt des Tooltips (Kopf, Text, Zeilen mit Farbschlüssel und Wert, Fuß); auch für die Satzbezüge (PF-GR-stil-1). */
export function TippBox({ inhalt, id }: { inhalt: TippInhalt; id?: string }): JSX.Element {
  return <div className="ki-tipp-inhalt" id={id}>
    <p className="ki-tipp-kopf">{inhalt.kopf}</p>
    {inhalt.text && <p className="ki-tipp-text">{inhalt.text}</p>}
    {(inhalt.zeilen ?? []).map((zeile, i) => <div key={`${i}-${zeile.name}`} className="ki-tipp-zeile">
      <span className={zeile.farbe ? `ki-tipp-schluessel ki-d-${zeile.farbe}` : 'ki-tipp-schluessel'} aria-hidden="true" />
      <span>{zeile.name}</span>
      <span className="ki-tipp-wert">{zeile.wert}</span>
    </div>)}
    {inhalt.fuss && <p className="ki-tipp-fuss">{inhalt.fuss}</p>}
  </div>;
}

/** Häkchen je Reihe; die letzte sichtbare Reihe lässt sich nicht abwählen (Satz DIAGRAMM.eineBleibt, bis sich die Wahl ändert).
 *  Mit wochenende dahinter der Schlüssel der Wochenend-Bänder (ohne Häkchen). */
export function DiagrammLegende({ reihen, sichtbar, onSichtbar, wochenende }: { reihen: DiagrammReihe[]; sichtbar: boolean[];
  onSichtbar: (neu: boolean[]) => void; wochenende?: boolean }): JSX.Element {
  const wahl = reihen.map((_, i) => sichtbar[i] !== false);
  const [bleibt, setBleibt] = useState(false);
  const schluessel = wahl.join('|');
  useEffect(() => { setBleibt(false); }, [schluessel]);
  const umschalten = (i: number) => {
    const neu = wahl.map((an, j) => j === i ? !an : an);
    if (!neu.some(Boolean)) { setBleibt(true); return; }
    onSichtbar(neu);
  };
  return <div className="ki-d-legende" role="group" aria-label={DIAGRAMM.legende}>
    {reihen.map((reihe, i) => <label key={reihe.name} className="ki-d-haekchen">
      <input type="checkbox" checked={wahl[i]} onChange={() => umschalten(i)} />
      <span className={`ki-d-schluessel ki-d-${reihe.farbe}`} aria-hidden="true" />
      {reihe.name}
    </label>)}
    {wochenende && <span className="ki-d-legende-eintrag">
      <span className="ki-d-schluessel ki-d-band-schluessel" aria-hidden="true" />
      {DIAGRAMM.wochenende}
    </span>}
    <p className="adm-sr-only" role="status">{bleibt ? DIAGRAMM.eineBleibt : ''}</p>
    {bleibt && <p className="ki-d-meldung">{DIAGRAMM.eineBleibt}</p>}
  </div>;
}

/** F-25 Entwurf C: Schlüssel der Linien (Zeitraum, Vorzeitraum, Hochrechnung, Ziel) neben den Häkchen; nur Anzeige, kein Knopf. */
export function LinienLegende({ eintraege }: { eintraege: { key: string; art: 'zeitraum' | 'vorzeitraum' | 'hochrechnung' | 'ziel'; text: string }[] }) {
  return <ul className="ki-d-linien-legende">{eintraege.map(e => <li key={e.key}>
    <span className={`ki-d-schluessel-linie ki-d-schluessel-${e.art}`} aria-hidden="true" />{e.text}</li>)}</ul>;
}

/** Lage des Tooltips (PF-SL5-tipp-1, Vergleich B3): über dem Wert, mittig zum Band; am rechten Rand nach links gekippt, links nie unter 0.
 *  Ohne Platz über dem Wert (oben im Diagramm oder über dem sichtbaren Teil der Rollfläche) unter der Fläche; ragt er dort über den
 *  sichtbaren unteren Rand, nach oben geschoben, bis er ganz sichtbar ist, nie über den sichtbaren oberen Rand. sichtOben und sichtUnten
 *  sind die Grenzen des sichtbaren Teils in Pixeln ab der Oberkante des Diagramms. */
export function tippLage(l: { cw: number; s: number; anker: { links: number; breite: number; oben: number; unten: number }; w: number; h: number;
  sichtOben: number; sichtUnten: number }): { links: number; hoch: number } {
  const a = l.anker;
  let links = (a.links + a.breite / 2) * l.s - l.w / 2;
  if (links + l.w > l.cw) links = (a.links + a.breite) * l.s - l.w;
  links = Math.max(0, Math.min(l.cw - l.w, links));
  let hoch = a.oben * l.s - l.h - 10;
  if (hoch < Math.max(0, l.sichtOben)) hoch = a.unten * l.s + 10;
  if (hoch + l.h > l.sichtUnten - 8) hoch = l.sichtUnten - 8 - l.h;
  return { links: Math.round(links), hoch: Math.round(Math.max(l.sichtOben, hoch)) };
}

/** Säulen oder Linie mit Achsen, Gitter und Tooltip. Ein Tab-Halt (role="img"); ← → wählen den Abschnitt, Pos1 und Ende den
 *  ersten bzw. letzten, Escape schließt. Maus: Überfahren; Finger: Antippen, Antippen daneben schließt. */
export default function Diagramm(props: DiagrammProps): JSX.Element {
  const { art, einheit, abschnitte, reihen, heute, linien = [], hoechstwert, hoechstIndex, wochenenden, markiert, zeige, vorlesen,
    flaeche, beschriftung, tipp, fuss, skala = 1 } = props;
  const wertText = props.wertText ?? zahl;
  const achsenText = props.achsenText ?? zahl;
  const id = useId();
  const behaelter = useRef<HTMLDivElement>(null);
  const tippRef = useRef<HTMLDivElement>(null);
  const [breite, setBreite] = useState(0);
  const [fenster, setFenster] = useState(() => typeof window === 'undefined'
    ? { breite: 1024, hoehe: 800 } : { breite: window.innerWidth, hoehe: window.innerHeight });
  const [aktiv, setAktiv] = useState<number | null>(null);
  // Ein Tooltip, den ein Satzbezug geöffnet hat (zeige), schließt nicht beim Antippen daneben, sondern mit zeige = null.
  const vonAussen = useRef(false);

  // Breite des Behälters (gerundet; neu gezeichnet nur bei geänderter Breite) und Fenstergröße für die Höhe.
  useMessEffekt(() => {
    const element = behaelter.current;
    if (!element) return;
    const messen = () => setBreite(Math.round(element.clientWidth));
    const fensterMessen = () => setFenster(vorher => vorher.breite === window.innerWidth && vorher.hoehe === window.innerHeight
      ? vorher : { breite: window.innerWidth, hoehe: window.innerHeight });
    messen();
    fensterMessen();
    const beobachter = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(messen);
    beobachter?.observe(element);
    window.addEventListener('resize', fensterMessen);
    return () => { beobachter?.disconnect(); window.removeEventListener('resize', fensterMessen); };
  }, []);

  // Von außen geöffnet oder geschlossen (Satzbezug), ohne den Fokus zu nehmen.
  useEffect(() => {
    if (zeige === undefined) return;
    if (zeige === null) { vonAussen.current = false; setAktiv(null); return; }
    if (zeige >= 0 && zeige < abschnitte.length) { vonAussen.current = true; setAktiv(zeige); }
  }, [zeige]); // eslint-disable-line react-hooks/exhaustive-deps

  // Antippen daneben schließt (Finger); gilt nicht für einen Tooltip, den ein Satzbezug geöffnet hat.
  useEffect(() => {
    if (aktiv === null) return;
    const daneben = (event: PointerEvent) => {
      if (vonAussen.current) return;
      if (behaelter.current && !behaelter.current.contains(event.target as Node)) setAktiv(null);
    };
    document.addEventListener('pointerdown', daneben);
    return () => document.removeEventListener('pointerdown', daneben);
  }, [aktiv]);

  // ---- Maße (Entwurf A bausteine.js:610-663 Säulen, :717-776 Linie)
  const saeulen = art === 'saeulen';
  const an = reihen.map((_, j) => props.sichtbar?.[j] !== false);
  const gezeigt = reihen.map((reihe, j) => ({ reihe, j })).filter(({ j }) => an[j]);
  // Säulen: die Summe der gezeigten Reihen; Linie: der größte gezeigte Wert ohne null (0, wenn alle null) – für Achse und Tooltip.
  const wert = (abschnitt: DiagrammAbschnitt) => {
    if (saeulen) return gezeigt.reduce((s, { j }) => s + (abschnitt.werte[j] ?? 0), 0);
    const mitWert = gezeigt.flatMap(({ j }) => { const v = abschnitt.werte[j]; return v == null ? [] : [v]; });
    return mitWert.length ? Math.max(...mitWert) : 0;
  };
  const werte = abschnitte.map(wert);
  const k = abschnitte.length;
  const W = Math.max(260, breite);
  const hoehe = props.hoehe ?? (saeulen ? { breit: 260, schmal: 210 } : { breit: 240, schmal: 200 });
  const H = fenster.breite < 700 ? hoehe.schmal
    : hoehe.breit === 'fenster' ? Math.max(200, Math.min(300, fenster.hoehe - 540)) : hoehe.breit;
  const m = saeulen ? { l: props.achsenText ? 46 : 40, r: 12, t: 30, b: 32 } : { l: 44, r: 16, t: 22, b: 32 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / Math.max(1, k);
  const linienBand = pw / Math.max(1, k - 1);
  const striche = teilstriche(Math.max(1, ...werte, ...linien.map(linie => linie.wert)) / skala).map(t => t * skala);
  const oben = striche[striche.length - 1];
  const y = (v: number) => m.t + ph - (v / oben) * ph;
  const x = (i: number) => saeulen ? m.l + i * band + band / 2 : m.l + (k <= 1 ? pw / 2 : (i / (k - 1)) * pw);
  // Band eines Abschnitts: bei Säulen die Spalte, bei der Linie um den Punkt, nie über die Fläche hinaus.
  const bandVon = (i: number) => saeulen ? m.l + i * band : Math.max(m.l, x(i) - linienBand / 2);
  const bandBreite = (i: number) => saeulen ? band : Math.min(W - m.r, x(i) + linienBand / 2) - bandVon(i);
  const saeulenBreite = Math.min(props.maxBreite ?? 24, Math.max(3, band * 0.64));
  const mitJahr = k > 0 && abschnitte[0].von.slice(0, 4) !== abschnitte[k - 1].bis.slice(0, 4);
  const hoechstensBeschriftet = W < 560 ? 4 : W < 900 ? 7 : k <= 12 ? 12 : 9;
  const jede = Math.max(1, Math.ceil(k / hoechstensBeschriftet));

  // Höchster sichtbarer Wert (bei Gleichstand der letzte) oder der Abschnitt aus hoechstIndex.
  let hoechst = -1;
  if (hoechstwert) {
    if (hoechstIndex !== undefined && hoechstIndex >= 0 && hoechstIndex < k) hoechst = hoechstIndex;
    else { let groesster = 0; werte.forEach((v, i) => { if (v > 0 && v >= groesster) { groesster = v; hoechst = i; } }); }
  }

  const inhalt = (i: number): TippInhalt => {
    if (tipp) return tipp(i);
    const abschnitt = abschnitte[i];
    // Säulen: je gezeigter Reihe eine Zeile (null als 0), bei zweien „Zusammen“; Linie: nur Reihen mit Wert, ohne „Zusammen“.
    const zeilen: NonNullable<TippInhalt['zeilen']> = saeulen
      ? gezeigt.map(({ reihe, j }) => ({ farbe: reihe.farbe, name: reihe.name, wert: wertText(abschnitt.werte[j] ?? 0) }))
      : gezeigt.flatMap(({ reihe, j }) => { const v = abschnitt.werte[j]; return v == null ? [] : [{ farbe: reihe.farbe, name: reihe.name, wert: wertText(v) }]; });
    if (saeulen && zeilen.length > 1) zeilen.push({ name: DIAGRAMM.zusammen, wert: wertText(wert(abschnitt)) });
    return { kopf: beschriftung?.(i).kopf ?? tippKopf(einheit, abschnitt.von, abschnitt.bis, heute), zeilen, fuss: fuss?.(i) };
  };
  const anker = (i: number) => saeulen
    ? { links: m.l + i * band, breite: band, oben: y(werte[i] ?? 0) - 4, unten: H - m.b }
    : { links: x(i) - 10, breite: 20, oben: Math.min(y(werte[i] ?? 0), H - m.b) - 8, unten: H - m.b };

  const offen = aktiv !== null && aktiv < k ? aktiv : null;
  const tippInhalt = offen === null ? null : inhalt(offen);

  // Tooltip nach tippLage: über dem Wert, sonst darunter, immer im sichtbaren Teil von .seiten-rahmen-inhalt (ohne sie: Browserfenster).
  useMessEffekt(() => {
    const element = behaelter.current, box = tippRef.current;
    if (!element || !box || offen === null) return;
    const cw = element.clientWidth, s = cw / W, a = anker(offen);
    const rahmen = element.getBoundingClientRect();
    const rolle = element.closest('.seiten-rahmen-inhalt')?.getBoundingClientRect();
    // Bildschirm-px in px des Bausteins (F-27: im Fenster mit zoom); cw, Anker und Kastengröße sind schon Baustein-px.
    const z = zoomVon(element);
    const sichtOben = (Math.max(rolle?.top ?? 0, 0) - rahmen.top) / z;
    const sichtUnten = (Math.min(rolle?.bottom ?? window.innerHeight, window.innerHeight) - rahmen.top) / z;
    const lage = tippLage({ cw, s, anker: a, w: box.offsetWidth, h: box.offsetHeight, sichtOben, sichtUnten });
    box.style.left = `${lage.links}px`;
    box.style.top = `${lage.hoch}px`;
  });

  // ---- Bedienung
  const start = () => {
    if (!k) return null;
    let i = k - 1;
    // Linie: der letzte Abschnitt, in dem die erste gezeigte Reihe einen Wert hat (Zielkurve: heute, nicht das Monatsende).
    if (!saeulen && gezeigt.length) {
      const erste = gezeigt[0].j;
      let letzter = k - 1;
      while (letzter >= 0 && abschnitte[letzter].werte[erste] == null) letzter--;
      if (letzter >= 0) i = letzter;
    }
    while (i > 0 && enthaeltHeute(abschnitte[i], heute) && werte[i] === 0) i--;
    return i;
  };
  const zeiger = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = behaelter.current;
    if (!element || !k) return;
    const r = element.getBoundingClientRect(), s = r.width / W, px = (event.clientX - r.left) / s;
    let i: number | null;
    if (saeulen) { const spalte = Math.floor((px - m.l) / band); i = spalte >= 0 && spalte < k ? spalte : null; }
    else if (px < m.l - 10 || px > W - m.r + 10) i = null;
    else i = Math.max(0, Math.min(k - 1, k === 1 ? 0 : Math.round(((px - m.l) / pw) * (k - 1))));
    vonAussen.current = false;
    setAktiv(i);
  };
  const verlassen = (event: React.PointerEvent<HTMLDivElement>) => { if (event.pointerType === 'mouse') setAktiv(null); };
  const fokus = () => { vonAussen.current = false; setAktiv(vorher => vorher ?? start()); };
  const taste = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!k) return;
    let i = offen ?? k - 1;
    if (event.key === 'ArrowRight') i = Math.min(k - 1, i + 1);
    else if (event.key === 'ArrowLeft') i = Math.max(0, i - 1);
    else if (event.key === 'Home') i = 0;
    else if (event.key === 'End') i = k - 1;
    else if (event.key === 'Escape') {
      // Nur ein offener Tooltip verbraucht Escape; sonst schließt Escape das Fenster darum (F-26).
      if (offen !== null) { event.preventDefault(); setAktiv(null); }
      return;
    } else return;
    event.preventDefault();
    vonAussen.current = false;
    setAktiv(i);
  };

  // ---- Zeichnung
  const saeulenPfade = saeulen ? abschnitte.flatMap((abschnitt, i) => {
    const links = m.l + i * band + (band - saeulenBreite) / 2;
    const teile = gezeigt.map(({ reihe, j }) => ({ farbe: reihe.farbe, j, v: abschnitt.werte[j] ?? 0 }));
    const oberste = teile.reduce((zuletzt, teil, n) => teil.v > 0 ? n : zuletzt, -1);
    let basis = 0;
    return teile.map((teil, n) => {
      if (teil.v <= 0) return null;
      const unten = basis > 0 ? y(basis) - 2 : y(0), hoch = y(basis + teil.v), darauf = basis > 0;
      basis += teil.v;
      if (unten - hoch <= (darauf ? 0.5 : 0)) return null;
      return <path key={`${abschnitt.von}-${teil.j}`} className={`ki-d-${teil.farbe}`} d={saeule(links, hoch, saeulenBreite, unten - hoch, n === oberste)} />;
    });
  }) : [];
  // Linie (Entwurf C bausteine.js:736-761): je gezeigter Reihe ein Pfad aus den Abschnitten mit Wert, nach einer Lücke (null) ein
  // neues Stück; Fläche vom ersten bis zum letzten Abschnitt mit Wert; Endpunkt mit Endwert am letzten Wert. Die erste Reihe liegt oben.
  const zielLinien = linien.filter(linie => linie.art === 'ziel');
  const linienReihen = saeulen ? [] : gezeigt.map(({ reihe, j }) => {
    const stellen = abschnitte.flatMap((abschnitt, i) => { const v = abschnitt.werte[j]; return v == null ? [] : [{ i, v }]; });
    const pfad = stellen.map(({ i, v }, n) => `${n === 0 || stellen[n - 1].i !== i - 1 ? 'M' : 'L'}${rund(x(i))},${rund(y(v))}`).join(' ');
    const erste = stellen[0], letzte = stellen[stellen.length - 1];
    const flaechePfad = erste && (reihe.flaeche || (j === 0 && flaeche))
      ? `M${rund(x(erste.i))},${rund(y(0))} ${stellen.map(({ i, v }) => `L${rund(x(i))},${rund(y(v))}`).join(' ')} L${rund(x(letzte.i))},${rund(y(0))} Z` : '';
    const strich = reihe.strich === 'gestrichelt' ? ' ki-d-strich-gestrichelt' : reihe.strich === 'punktiert' ? ' ki-d-strich-punktiert' : '';
    const klasse = `ki-d-linie ki-d-linie-${reihe.farbe}${j === 0 ? ' ki-d-linie-erste' : ''}${strich}`;
    let ende: { cx: number; cy: number; text: string; tx: number; ty: number; anker: 'start' | 'end' } | null = null;
    if (letzte && reihe.endText) {
      const cx = x(letzte.i), cy = y(letzte.v), rechts = W - m.r - cx > 70;
      const darunter = zielLinien.some(linie => Math.abs(cy - y(linie.wert)) < 26 && cy > y(linie.wert));
      ende = { cx, cy, text: reihe.endText, tx: rechts ? cx + 10 : cx - 10, ty: darunter ? cy + 22 : cy - 10, anker: rechts ? 'start' : 'end' };
    }
    return { reihe, j, pfad, flaechePfad, klasse, ende };
  }).reverse();
  // Waagrechte Linien („Schnitt“, „davor“, „Ziel“): bei Säulen über den Säulen, bei der Linie unter den Linien (wie im Entwurf C).
  const waagrecht = linien.map((linie, i) => {
    const hoch = Math.round(y(linie.wert)) + 0.5, links = linie.seite === 'links';
    return <g key={`linie-${i}`}>
      <line className={linie.art === 'ziel' ? 'ki-d-ziel' : linie.art === 'vorher' ? 'ki-d-vorher' : 'ki-d-schnitt'} x1={m.l} x2={W - m.r} y1={hoch} y2={hoch} />
      <text className="ki-d-text-stark ki-d-halo" x={links ? m.l + 6 : W - m.r - 2} y={hoch - 7} textAnchor={links ? 'start' : 'end'}>{linie.text}</text>
    </g>;
  });

  return <>
    <div ref={behaelter} className="ki-diagramm" tabIndex={0} role="img" aria-roledescription={DIAGRAMM.rolle} aria-label={`${vorlesen} ${DIAGRAMM.tasten}`}
      onPointerMove={zeiger} onPointerDown={zeiger} onPointerLeave={verlassen} onFocus={fokus} onBlur={() => setAktiv(null)} onKeyDown={taste}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} aria-hidden="true" focusable="false">
        <g>
          {wochenenden && einheit === 'TAG' && abschnitte.map((abschnitt, i) => wochentag(abschnitt.von) >= 5
            ? <rect key={`band-${abschnitt.von}`} className="ki-d-band" x={rund(bandVon(i))} y={m.t} width={rund(bandBreite(i))} height={ph} /> : null)}
          {markiert && abschnitte.map((abschnitt, i) => markiert(i)
            ? <rect key={`markiert-${abschnitt.von}`} className="ki-d-markiert" x={rund(bandVon(i))} y={m.t} width={rund(bandBreite(i))} height={ph} /> : null)}
          {saeulen && offen !== null && <rect className="ki-d-hover" x={rund(bandVon(offen))} y={m.t} width={rund(band)} height={ph} />}
        </g>
        {striche.map(t => <g key={t}>
          <line className={t === 0 ? 'ki-d-achse' : 'ki-d-raster'} x1={m.l} x2={W - m.r} y1={Math.round(y(t)) + 0.5} y2={Math.round(y(t)) + 0.5} />
          <text className="ki-d-text" x={m.l - 8} y={rund(y(t) + 5)} textAnchor="end">{achsenText(t)}</text>
        </g>)}
        {saeulenPfade}
        {saeulen && waagrecht}
        {linienReihen.map(l => l.flaechePfad ? <path key={`flaeche-${l.j}`} className="ki-d-flaeche" d={l.flaechePfad} /> : null)}
        {!saeulen && waagrecht}
        {linienReihen.map(l => l.pfad ? <path key={`pfad-${l.j}`} className={l.klasse} d={l.pfad} /> : null)}
        {linienReihen.map(l => l.ende && <g key={`ende-${l.j}`}>
          <circle className={`ki-d-punkt ki-d-punkt-${l.reihe.farbe}`} cx={rund(l.ende.cx)} cy={rund(l.ende.cy)} r={5} />
          <text className="ki-d-text-stark ki-d-halo" x={rund(l.ende.tx)} y={rund(l.ende.ty)} textAnchor={l.ende.anker}>{l.ende.text}</text>
        </g>)}
        {hoechstwert && hoechst >= 0 && werte[hoechst] > 0 && <text className="ki-d-text-stark ki-d-halo" textAnchor="middle"
          x={rund(Math.max(m.l + 70, Math.min(W - m.r - 70, x(hoechst))))} y={rund(y(werte[hoechst]) - 10)}>{hoechstwert(hoechst, werte[hoechst])}</text>}
        {abschnitte.map((abschnitt, i) => {
          if (i % jede !== 0) return null;
          const erste = !saeulen && i === 0 && k > 1;
          return <text key={`achse-${abschnitt.von}`} className="ki-d-text" x={rund(erste ? x(i) - 4 : x(i))} y={H - 8} textAnchor={erste ? 'start' : 'middle'}>
            {beschriftung?.(i).achse ?? achseText(einheit, abschnitt.von, mitJahr)}</text>;
        })}
        {!saeulen && offen !== null && <g>
          <line className="ki-d-faden" x1={rund(x(offen))} x2={rund(x(offen))} y1={m.t} y2={m.t + ph} />
          {linienReihen.map(({ reihe, j }) => {
            const v = abschnitte[offen].werte[j];
            return v == null ? null : <circle key={`punkt-${j}`} className={`ki-d-punkt ki-d-punkt-${reihe.farbe}`} cx={rund(x(offen))} cy={rund(y(v))} r={5} />;
          })}
        </g>}
      </svg>
      {tippInhalt && <div ref={tippRef} className={saeulen ? 'ki-tipp ki-tipp-saeulen' : 'ki-tipp'} role="tooltip" id={`${id}-tipp`}>
        <TippBox inhalt={tippInhalt} />
      </div>}
    </div>
    <p className="adm-sr-only" aria-live="polite">{tippInhalt ? vorlesText(tippInhalt) : ''}</p>
  </>;
}
